import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LogOut, Users, Settings2, BarChart3, TrendingUp, BookOpen, Star, LayoutDashboard, CreditCard, LifeBuoy, Receipt } from "lucide-react";

function Metric({ label, value, sub, icon: Icon, color = "#1F3A8A" }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`metric-${label.replace(/\s/g,'-').toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <Icon size={18} style={{ color }}/>
      </div>
      <div className="mt-2 font-heading text-3xl font-extrabold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function OverviewTab() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/admin/metrics").then(r => setM(r.data)); }, []);
  if (!m) return <div className="text-slate-500">Carregando métricas...</div>;
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric label="Total de usuários" value={m.total_users} icon={Users}/>
        <Metric label="Premium" value={m.premium_users} sub={`${m.free_users} grátis`} icon={Star} color="#8B5CF6"/>
        <Metric label="Receita mensal" value={`R$ ${m.monthly_revenue_estimate.toFixed(2).replace(".",",")}`} icon={TrendingUp} color="#10b981"/>
        <Metric label="Assinaturas ativas" value={m.active_subscriptions} icon={BarChart3} color="#3B82F6"/>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric label="Materiais totais" value={m.total_materiais} icon={BookOpen}/>
        <Metric label="Gerações totais" value={m.total_generations} icon={BarChart3} color="#8B5CF6"/>
        <Metric label="Gerações hoje" value={m.today_generations} icon={TrendingUp} color="#FBBF24"/>
        <Metric label="Novos usuários hoje" value={m.new_users_today} icon={Users} color="#3B82F6"/>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">Materiais por tipo</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Object.entries(m.materiais_by_type).map(([k,v]) => (
            <div key={k} className="text-center p-4 rounded-xl bg-slate-50">
              <div className="text-2xl font-heading font-extrabold text-[#1F3A8A]">{v}</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mt-1">{k}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/admin/users").then(r => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const patch = async (uid, body) => {
    try { await api.patch(`/admin/users/${uid}`, body); await load(); toast.success("Atualizado."); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (uid) => {
    if (!window.confirm("Excluir este usuário e todos os seus materiais?")) return;
    try { await api.delete(`/admin/users/${uid}`); await load(); toast.success("Excluído."); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-widest">
          <tr>
            <th className="text-left p-4">Nome</th>
            <th className="text-left p-4">Email</th>
            <th className="text-left p-4">Plano</th>
            <th className="text-left p-4">Status</th>
            <th className="text-left p-4">Cadastro</th>
            <th className="text-right p-4">Ações</th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => (
            <tr key={u.id} className="border-t border-slate-100" data-testid={`user-row-${u.id}`}>
              <td className="p-4 font-semibold">{u.name}</td>
              <td className="p-4 text-slate-600">{u.email}</td>
              <td className="p-4">
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${u.plan === "premium" ? "bg-[#8B5CF6]/15 text-[#8B5CF6]" : "bg-slate-100 text-slate-600"}`}>
                  {u.plan === "premium"
                    ? (u.subscription_source === "asaas" ? "Premium — Asaas"
                       : u.subscription_source === "payment" ? "Premium — Pagamento"
                       : u.subscription_source === "manual" ? "Premium — Manual"
                       : "Premium")
                    : "free"}
                </span>
              </td>
              <td className="p-4">
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${u.status === "blocked" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                  {u.status}
                </span>
              </td>
              <td className="p-4 text-slate-500">{new Date(u.created_at).toLocaleDateString("pt-BR")}</td>
              <td className="p-4 text-right space-x-1">
                {u.plan === "premium" ? (
                  <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => patch(u.id, { plan: "free" })} data-testid={`btn-free-${u.id}`}>Tornar Free</Button>
                ) : (
                  <Button size="sm" className="rounded-full bg-[#8B5CF6] hover:bg-[#7c4ef4] text-xs" onClick={() => patch(u.id, { plan: "premium" })} data-testid={`btn-premium-${u.id}`}>Ativar Premium</Button>
                )}
                {u.status === "blocked" ? (
                  <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => patch(u.id, { status: "active" })}>Desbloquear</Button>
                ) : (
                  u.role !== "admin" && <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => patch(u.id, { status: "blocked" })}>Bloquear</Button>
                )}
                {u.role !== "admin" && <Button size="sm" variant="outline" className="rounded-full text-xs text-red-600 border-red-200" onClick={() => remove(u.id)}>Excluir</Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SubscriptionsTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/asaas/subscriptions", { params: q ? { q } : {} }).then(r => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
      <div className="p-4 flex gap-2 border-b border-slate-100">
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por e-mail, ID da assinatura ou cliente..." className="rounded-xl" data-testid="sub-search"/>
        <Button onClick={load} className="rounded-full bg-[#1F3A8A]" data-testid="sub-search-btn">Buscar</Button>
      </div>
      {items.length === 0 ? (
        <div className="p-8 text-center text-slate-500">Nenhuma assinatura Asaas registrada ainda.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-4">E-mail</th>
              <th className="text-left p-4">Subscription ID</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Valor</th>
              <th className="text-left p-4">Próximo venc.</th>
              <th className="text-left p-4">Ciclo</th>
              <th className="text-left p-4">Atualizado</th>
            </tr>
          </thead>
          <tbody>
            {items.map(s => (
              <tr key={s.subscription_id || s.user_id} className="border-t border-slate-100" data-testid={`sub-row-${s.subscription_id}`}>
                <td className="p-4 text-slate-700">{s.email_masked || s.email || "—"}</td>
                <td className="p-4 font-mono text-xs text-slate-500">{s.subscription_id || "—"}</td>
                <td className="p-4">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                    s.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" :
                    s.status === "EXPIRED" || s.status === "INACTIVE" ? "bg-slate-200 text-slate-600" :
                    s.status === "OVERDUE" ? "bg-amber-100 text-amber-700" :
                    "bg-slate-100 text-slate-600"
                  }`}>{s.status || "—"}</span>
                </td>
                <td className="p-4 text-slate-700">{s.value ? `R$ ${Number(s.value).toFixed(2).replace(".",",")}` : "—"}</td>
                <td className="p-4 text-slate-500">{s.next_due_date || "—"}</td>
                <td className="p-4 text-slate-500">{s.cycle || "—"}</td>
                <td className="p-4 text-slate-500">{s.updated_at ? new Date(s.updated_at).toLocaleString("pt-BR") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PaymentsTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/asaas/payments", { params: q ? { q } : {} }).then(r => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
      <div className="p-4 flex gap-2 border-b border-slate-100">
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por ID, status, usuário..." className="rounded-xl" data-testid="pay-search"/>
        <Button onClick={load} className="rounded-full bg-[#1F3A8A]" data-testid="pay-search-btn">Buscar</Button>
      </div>
      {items.length === 0 ? (
        <div className="p-8 text-center text-slate-500">Nenhum pagamento Asaas registrado ainda.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-4">Payment ID</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Tipo</th>
              <th className="text-left p-4">Valor</th>
              <th className="text-left p-4">Vencimento</th>
              <th className="text-left p-4">Pago em</th>
              <th className="text-left p-4">Assinatura</th>
            </tr>
          </thead>
          <tbody>
            {items.map(p => (
              <tr key={p.payment_id} className="border-t border-slate-100" data-testid={`pay-row-${p.payment_id}`}>
                <td className="p-4 font-mono text-xs text-slate-500">{p.payment_id}</td>
                <td className="p-4">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                    p.status === "CONFIRMED" || p.status === "RECEIVED" ? "bg-emerald-100 text-emerald-700" :
                    p.status === "OVERDUE" ? "bg-amber-100 text-amber-700" :
                    p.status === "REFUNDED" || p.status === "CHARGEBACK_REQUESTED" ? "bg-red-100 text-red-700" :
                    "bg-slate-100 text-slate-600"
                  }`}>{p.status}</span>
                </td>
                <td className="p-4 text-slate-500">{p.billing_type || "—"}</td>
                <td className="p-4 text-slate-700">{p.value ? `R$ ${Number(p.value).toFixed(2).replace(".",",")}` : "—"}</td>
                <td className="p-4 text-slate-500">{p.due_date || "—"}</td>
                <td className="p-4 text-slate-500">{p.payment_date || "—"}</td>
                <td className="p-4 font-mono text-xs text-slate-500">{p.subscription_id || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TicketsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const load = () => api.get("/admin/support/tickets", { params: statusFilter ? { status: statusFilter } : {} })
    .then(r => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const send = async (newStatus) => {
    if (!open) return;
    try {
      const body = {};
      if (reply.trim()) body.message = reply.trim();
      if (newStatus) body.status = newStatus;
      const { data } = await api.patch(`/admin/support/tickets/${open.id}`, body);
      setOpen(data); setReply(""); load();
      toast.success("Chamado atualizado.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-3 border-b flex gap-2">
          {["", "open", "in_progress", "resolved"].map(s => (
            <button key={s||"all"} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1 rounded-full text-xs font-semibold ${statusFilter === s ? "bg-[#1F3A8A] text-white" : "bg-slate-100 text-slate-600"}`}
              data-testid={`tickets-filter-${s||"all"}`}>
              {s === "" ? "Todos" : s === "open" ? "Abertos" : s === "in_progress" ? "Em atendimento" : "Resolvidos"}
            </button>
          ))}
        </div>
        <div className="max-h-[600px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-slate-500 text-sm">Sem chamados.</div>
          ) : items.map(t => (
            <button key={t.id} onClick={() => setOpen(t)}
              className={`w-full text-left p-4 border-b border-slate-100 hover:bg-slate-50 ${open?.id === t.id ? "bg-slate-50" : ""}`}
              data-testid={`admin-ticket-${t.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-slate-900 truncate">#{t.number} {t.subject}</div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${t.status === "resolved" ? "bg-emerald-100 text-emerald-700" : t.status === "in_progress" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
                  {t.status}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-1 truncate">{t.user_email} · {t.category}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        {!open ? (
          <div className="text-slate-500 text-sm">Selecione um chamado para visualizar.</div>
        ) : (
          <>
            <div className="text-xs uppercase tracking-widest text-[#8B5CF6] font-bold">#{open.number} · {open.category}</div>
            <h3 className="font-heading text-lg font-bold text-slate-900">{open.subject}</h3>
            <div className="text-xs text-slate-500 mt-1">{open.user_name} · {open.user_email}</div>
            <div className="mt-4 space-y-3 max-h-80 overflow-y-auto">
              {(open.messages || []).map(m => (
                <div key={m.id} className={`p-3 rounded-xl ${m.author === "admin" ? "bg-[#1F3A8A]/5 border-l-4 border-[#1F3A8A]" : "bg-slate-50"}`}>
                  <div className="text-xs text-slate-500 font-semibold mb-1">{m.author_name} · {new Date(m.created_at).toLocaleString("pt-BR")}</div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap">{m.message}</div>
                </div>
              ))}
            </div>
            <Textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Resposta ao usuário (enviada por e-mail e visível no chamado)" className="mt-3 rounded-xl" data-testid="admin-ticket-reply"/>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button onClick={() => send(null)} disabled={!reply.trim()} className="rounded-full bg-[#1F3A8A]" data-testid="admin-ticket-send">Enviar resposta</Button>
              <Button onClick={() => send("in_progress")} variant="outline" className="rounded-full" data-testid="admin-ticket-progress">Marcar em atendimento</Button>
              <Button onClick={() => send("resolved")} variant="outline" className="rounded-full" data-testid="admin-ticket-resolve">Resolver</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ConfigTab() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/admin/config").then(r => setCfg(r.data)); }, []);
  const save = async () => {
    try {
      const { data } = await api.put("/admin/config", {
        free_limit: Number(cfg.free_limit),
        premium_limit: Number(cfg.premium_limit),
        premium_price: Number(cfg.premium_price),
        system_message: cfg.system_message,
      });
      setCfg(data); toast.success("Configurações salvas.");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!cfg) return <div className="text-slate-500">Carregando...</div>;
  return (
    <div className="max-w-xl bg-white rounded-2xl border border-slate-200 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-4">
      <div>
        <Label className="text-sm font-semibold text-slate-700">Gerações gratuitas</Label>
        <Input type="number" value={cfg.free_limit} onChange={e => setCfg({...cfg, free_limit: e.target.value})} className="rounded-xl mt-1 h-11" data-testid="config-free"/>
      </div>
      <div>
        <Label className="text-sm font-semibold text-slate-700">Limite Premium (mensal)</Label>
        <Input type="number" value={cfg.premium_limit} onChange={e => setCfg({...cfg, premium_limit: e.target.value})} className="rounded-xl mt-1 h-11" data-testid="config-premium"/>
      </div>
      <div>
        <Label className="text-sm font-semibold text-slate-700">Preço do Premium (R$)</Label>
        <Input type="number" step="0.01" value={cfg.premium_price} onChange={e => setCfg({...cfg, premium_price: e.target.value})} className="rounded-xl mt-1 h-11" data-testid="config-price"/>
      </div>
      <div>
        <Label className="text-sm font-semibold text-slate-700">Mensagem do sistema</Label>
        <Input value={cfg.system_message} onChange={e => setCfg({...cfg, system_message: e.target.value})} className="rounded-xl mt-1 h-11" data-testid="config-message"/>
      </div>
      <Button onClick={save} className="rounded-full bg-[#1F3A8A] hover:bg-[#15265b]" data-testid="btn-save-config">Salvar Configurações</Button>
    </div>
  );
}

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Logo/>
            <span className="hidden sm:inline-block text-xs uppercase tracking-[0.25em] font-bold bg-[#FBBF24]/20 text-[#0F172A] px-3 py-1 rounded-full">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/dashboard"><Button variant="ghost" className="rounded-full" data-testid="admin-back-dashboard"><LayoutDashboard size={16} className="mr-2"/>App</Button></Link>
            <Button variant="outline" className="rounded-full" onClick={() => { logout(); nav("/"); }} data-testid="admin-logout">
              <LogOut size={16} className="mr-2"/> Sair
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">Painel do Administrador</h1>
        <p className="mt-2 text-slate-600">Bem-vindo, {user?.name}.</p>

        <Tabs defaultValue="overview" className="mt-8">
          <TabsList className="rounded-full bg-white border border-slate-200 p-1 flex-wrap">
            <TabsTrigger value="overview" className="rounded-full" data-testid="tab-overview"><BarChart3 size={14} className="mr-1.5"/>Visão geral</TabsTrigger>
            <TabsTrigger value="users" className="rounded-full" data-testid="tab-users"><Users size={14} className="mr-1.5"/>Usuários</TabsTrigger>
            <TabsTrigger value="subs" className="rounded-full" data-testid="tab-subs"><CreditCard size={14} className="mr-1.5"/>Assinaturas</TabsTrigger>
            <TabsTrigger value="payments" className="rounded-full" data-testid="tab-payments"><Receipt size={14} className="mr-1.5"/>Pagamentos</TabsTrigger>
            <TabsTrigger value="tickets" className="rounded-full" data-testid="tab-tickets"><LifeBuoy size={14} className="mr-1.5"/>Suporte</TabsTrigger>
            <TabsTrigger value="config" className="rounded-full" data-testid="tab-config"><Settings2 size={14} className="mr-1.5"/>Configurações</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-6"><OverviewTab/></TabsContent>
          <TabsContent value="users" className="mt-6"><UsersTab/></TabsContent>
          <TabsContent value="subs" className="mt-6"><SubscriptionsTab/></TabsContent>
          <TabsContent value="payments" className="mt-6"><PaymentsTab/></TabsContent>
          <TabsContent value="tickets" className="mt-6"><TicketsTab/></TabsContent>
          <TabsContent value="config" className="mt-6"><ConfigTab/></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
